import { useState, useEffect } from 'react';
import { admin as adminApi } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { Save, Loader2, Settings, Layers, Server, Mail, Send, RotateCcw, Plus, Trash2, ChevronDown, ChevronUp, Gauge, Download, Upload, Brain, Zap } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { llm as llmApi } from '@/lib/api';

const TEMPLATE_META = {
  welcome: { label: 'Welcome (New Signup)', vars: ['{user_name}', '{email}', '{dashboard_url}'] },
  low_balance_alert: { label: 'Low Balance Alert', vars: ['{user_name}', '{balance}', '{threshold}', '{topup_url}'] },
  balance_depleted: { label: 'Balance Depleted (API Blocked)', vars: ['{user_name}', '{topup_url}'] },
  topup_confirmation: { label: 'Top-Up Confirmation', vars: ['{user_name}', '{amount}', '{new_balance}', '{dashboard_url}'] },
  password_reset: { label: 'Password Reset', vars: ['{user_name}', '{token}', '{reset_url}'] },
  auto_topup_failed: { label: 'Auto Top-Up Failed', vars: ['{user_name}', '{amount}', '{balance}', '{topup_url}'] },
  new_signup_admin: { label: 'New Signup (Admin Notification)', vars: ['{user_name}', '{email}', '{signup_date}'] },
};

export default function ConfigEditor() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('welcome');
  const [expandedSvc, setExpandedSvc] = useState({});
  const [fetchedModels, setFetchedModels] = useState({ openrouter: [], anthropic: [] });
  const [fetchingModels, setFetchingModels] = useState('');
  const [modelSearch, setModelSearch] = useState('');

  useEffect(() => { loadConfig(); }, []);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const res = await adminApi.config();
      setConfig(res.data);
    } catch {}
    setLoading(false);
  };

  const updateField = (path, value) => {
    setConfig(prev => {
      const c = JSON.parse(JSON.stringify(prev));
      const parts = path.split('.');
      let obj = c;
      for (let i = 0; i < parts.length - 1; i++) {
        if (obj[parts[i]] === undefined || obj[parts[i]] === null) obj[parts[i]] = {};
        obj = obj[parts[i]];
      }
      obj[parts[parts.length - 1]] = value;
      return c;
    });
  };

  const saveSection = async (section) => {
    setSaving(section);
    try {
      const data = {};
      if (section === 'services') data.services = config.services;
      else if (section === 'plan_limits') data.plan_limits = config.plan_limits;
      else if (section === 'gpu_server') data.gpu_server = config.gpu_server;
      else if (section === 'stripe') data.stripe = config.stripe;
      else if (section === 'gateway') data.gateway = config.gateway;
      else if (section === 'welcome_credit') data.welcome_credit = config.welcome_credit;
      else if (section === 'email') data.email = config.email;
      else if (section === 'email_templates') data.email_templates = config.email_templates;
      else if (section === 'security') data.security = config.security;
      else if (section === 'llm_timeout_seconds') data.llm_timeout_seconds = config.llm_timeout_seconds;
      else if (section === 'llm_providers') data.llm_providers = config.llm_providers;
      else if (section === 'llm_tier_models') data.llm_tier_models = config.llm_tier_models;

      await adminApi.bulkUpdateConfig({ config: data });
      toast.success(`${section} saved successfully`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
    setSaving('');
  };

  if (loading || !config) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  }

  const tierLabels = { economy: 'Economy', standard: 'Standard', priority: 'Priority' };
  const tierColors = { economy: 'text-slate-400', standard: 'text-cyan-400', priority: 'text-amber-400' };

  return (
    <div data-testid="config-editor" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Config Editor</h2>
          <p className="text-muted-foreground mt-1">Edit platform configuration. Changes take effect immediately.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={async () => {
            try {
              const res = await adminApi.exportSettings();
              const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a'); a.href = url; a.download = `tensornex-settings-${new Date().toISOString().slice(0,10)}.json`; a.click();
              URL.revokeObjectURL(url);
              toast.success('Settings exported');
            } catch (err) { toast.error('Export failed'); }
          }}><Download className="w-3 h-3 mr-1.5" />Export JSON</Button>
          <Button variant="outline" size="sm" onClick={() => {
            const input = document.createElement('input'); input.type = 'file'; input.accept = '.json';
            input.onchange = async (e) => {
              const file = e.target.files[0]; if (!file) return;
              try {
                const text = await file.text();
                const data = JSON.parse(text);
                if (!data.platform_config && !data.landing_content) { toast.error('Invalid settings file'); return; }
                if (!window.confirm('Import these settings? This will OVERWRITE your current configuration.')) return;
                await adminApi.importSettings({ data, overwrite: true });
                toast.success('Settings imported! Reloading...');
                setTimeout(() => window.location.reload(), 1000);
              } catch (err) { toast.error(err.response?.data?.detail || 'Import failed'); }
            };
            input.click();
          }}><Upload className="w-3 h-3 mr-1.5" />Import JSON</Button>
        </div>
      </div>

      <Tabs defaultValue="services">
        <TabsList className="bg-secondary/50">
          <TabsTrigger value="services"><Settings className="w-3 h-3 mr-1.5" />Services</TabsTrigger>
          {/* Plans tab hidden — wallet-based billing, no plan tiers */}
          <TabsTrigger value="gpu"><Server className="w-3 h-3 mr-1.5" />GPU Server</TabsTrigger>
          <TabsTrigger value="gateway"><Gauge className="w-3 h-3 mr-1.5" />Gateway</TabsTrigger>
          <TabsTrigger value="llm"><Brain className="w-3 h-3 mr-1.5" />LLM Providers</TabsTrigger>
          <TabsTrigger value="email"><Mail className="w-3 h-3 mr-1.5" />Email</TabsTrigger>
        </TabsList>

        <TabsContent value="services" className="space-y-4 mt-4">
          {config.services && Object.entries(config.services).map(([svcId, svc]) => {
            const isExpanded = expandedSvc[svcId];
            const fp = svc.feature_page || {};
            return (
            <Card key={svcId} data-testid={`config-service-${svcId}`} className="bg-card border-border/50">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CardTitle className="text-base">{svc.name}</CardTitle>
                    <span className="text-[10px] font-mono text-muted-foreground bg-secondary/50 px-1.5 py-0.5 rounded">{svcId}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Label className="text-xs text-muted-foreground">Available</Label>
                    <Switch data-testid={`toggle-${svcId}`} checked={svc.is_available} onCheckedChange={v => updateField(`services.${svcId}.is_available`, v)} />
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => setExpandedSvc(p => ({ ...p, [svcId]: !p[svcId] }))}>
                      {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-destructive hover:bg-destructive/10" onClick={() => {
                      if (window.confirm(`Delete service "${svc.name}"? This removes it from the platform.`)) {
                        setConfig(prev => { const c = JSON.parse(JSON.stringify(prev)); delete c.services[svcId]; return c; });
                      }
                    }}><Trash2 className="w-3 h-3" /></Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Basic Info */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1"><Label className="text-xs">Name</Label><Input value={svc.name || ''} onChange={e => updateField(`services.${svcId}.name`, e.target.value)} className="h-7 text-xs" /></div>
                  <div className="space-y-1"><Label className="text-xs">Category</Label>
                    <Select value={svc.category || 'database'} onValueChange={v => updateField(`services.${svcId}.category`, v)}>
                      <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ml_model">ML Model</SelectItem><SelectItem value="database">Database</SelectItem>
                        <SelectItem value="streaming">Streaming</SelectItem><SelectItem value="storage">Storage</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1"><Label className="text-xs">Short Description</Label><Input value={svc.description || ''} onChange={e => updateField(`services.${svcId}.description`, e.target.value)} className="h-7 text-xs" /></div>
                </div>

                {/* Pricing Tiers */}
                <div className="grid grid-cols-3 gap-4">
                  {Object.entries(svc.pricing || {}).map(([tier, pricing]) => (
                    <div key={tier} className="space-y-2 p-3 rounded-lg bg-secondary/20 border border-border/20">
                      <p className={`text-xs font-semibold uppercase tracking-wider ${tierColors[tier]}`}>{tierLabels[tier]}</p>
                      <div className="space-y-1.5">
                        <div className="flex items-center gap-2"><Label className="text-xs w-14">Rate</Label><div className="flex items-center gap-1 flex-1"><span className="text-xs text-muted-foreground">$</span><Input type="number" step="0.01" value={pricing.hourly_rate} onChange={e => updateField(`services.${svcId}.pricing.${tier}.hourly_rate`, parseFloat(e.target.value) || 0)} className="h-7 text-xs font-mono" /><span className="text-xs text-muted-foreground">/hr</span></div></div>
                        <div className="flex items-center gap-2"><Label className="text-xs w-14">Latency</Label><div className="flex items-center gap-1 flex-1"><Input type="number" step="1" value={pricing.max_latency_seconds || pricing.timeout_seconds || 10} onChange={e => updateField(`services.${svcId}.pricing.${tier}.max_latency_seconds`, parseInt(e.target.value) || 1)} className="h-7 text-xs font-mono" /><span className="text-xs text-muted-foreground">sec</span></div></div>
                        <div className="flex items-center gap-2"><Label className="text-xs w-14">Req/day</Label><div className="flex items-center gap-1 flex-1"><Input type="number" step="100" value={pricing.max_requests_per_day || 1000} onChange={e => updateField(`services.${svcId}.pricing.${tier}.max_requests_per_day`, parseInt(e.target.value) || -1)} className="h-7 text-xs font-mono" /><span className="text-xs text-muted-foreground">-1=no limit</span></div></div>
                        <div className="flex items-center gap-2"><Label className="text-xs w-14">GPU T/O</Label><div className="flex items-center gap-1 flex-1"><Input type="number" step="5" value={pricing.gpu_call_timeout_seconds || 30} onChange={e => updateField(`services.${svcId}.pricing.${tier}.gpu_call_timeout_seconds`, parseInt(e.target.value) || 30)} className="h-7 text-xs font-mono" /><span className="text-xs text-muted-foreground">sec (GPU call limit)</span></div></div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Feature Page Content (collapsible) */}
                {isExpanded && (
                  <div className="border-t border-border/30 pt-4 space-y-3">
                    <p className="text-xs font-bold uppercase tracking-wider text-primary">Feature Page Content</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1"><Label className="text-xs">Tagline</Label><Input value={fp.tagline || svc.friendly_description || ''} onChange={e => updateField(`services.${svcId}.feature_page.tagline`, e.target.value)} className="h-8 text-xs" placeholder="One-line description" /></div>
                      <div className="space-y-1"><Label className="text-xs">Friendly Description</Label><Input value={svc.friendly_description || ''} onChange={e => updateField(`services.${svcId}.friendly_description`, e.target.value)} className="h-8 text-xs" placeholder="Human-friendly description" /></div>
                    </div>
                    <div className="space-y-1"><Label className="text-xs">Use Cases (comma separated)</Label><Input value={(svc.use_cases || []).join(', ')} onChange={e => updateField(`services.${svcId}.use_cases`, e.target.value.split(',').map(s => s.trim()).filter(Boolean))} className="h-8 text-xs" /></div>
                    <div className="space-y-1"><Label className="text-xs">Overview (shown on feature page)</Label>
                      <textarea value={fp.overview || ''} onChange={e => updateField(`services.${svcId}.feature_page.overview`, e.target.value)} className="w-full bg-background border border-input rounded-md p-3 text-xs font-mono resize-none h-28 leading-relaxed" placeholder="2-3 paragraphs about this service..." />
                    </div>
                    <div className="space-y-1"><Label className="text-xs">How It Works on Our Platform</Label>
                      <textarea value={fp.how_it_works || ''} onChange={e => updateField(`services.${svcId}.feature_page.how_it_works`, e.target.value)} className="w-full bg-background border border-input rounded-md p-3 text-xs font-mono resize-none h-20 leading-relaxed" placeholder="How this service works on our platform vs traditional setup..." />
                    </div>
                    <div className="space-y-1"><Label className="text-xs">Related Services (comma separated IDs)</Label><Input value={(fp.works_with || []).join(', ')} onChange={e => updateField(`services.${svcId}.feature_page.works_with`, e.target.value.split(',').map(s => s.trim()).filter(Boolean))} className="h-8 text-xs font-mono" placeholder="qdrant, bge_m3, redis" /></div>

                    {/* AI Development Use Cases */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label className="text-xs">AI Development Use Cases</Label>
                        <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={() => {
                          const cases = [...(fp.ai_development || []), { title: 'New Use Case', description: '' }];
                          updateField(`services.${svcId}.feature_page.ai_development`, cases);
                        }}><Plus className="w-2.5 h-2.5 mr-1" />Add</Button>
                      </div>
                      {(fp.ai_development || []).map((uc, i) => (
                        <div key={i} className="flex gap-2 items-start p-2 rounded-lg bg-secondary/10 border border-border/20">
                          <div className="flex-1 space-y-1">
                            <Input value={uc.title || ''} onChange={e => { const cases = [...(fp.ai_development || [])]; cases[i] = { ...cases[i], title: e.target.value }; updateField(`services.${svcId}.feature_page.ai_development`, cases); }} className="h-6 text-xs" placeholder="Title" />
                            <textarea value={uc.description || ''} onChange={e => { const cases = [...(fp.ai_development || [])]; cases[i] = { ...cases[i], description: e.target.value }; updateField(`services.${svcId}.feature_page.ai_development`, cases); }} className="w-full bg-background border border-input rounded-md p-2 text-xs resize-none h-12" placeholder="Description..." />
                          </div>
                          <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-destructive shrink-0" onClick={() => { const cases = (fp.ai_development || []).filter((_, j) => j !== i); updateField(`services.${svcId}.feature_page.ai_development`, cases); }}><Trash2 className="w-2.5 h-2.5" /></Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
            );
          })}

          {/* Add New Service */}
          <div className="flex gap-2">
            <Button data-testid="save-services" onClick={() => saveSection('services')} disabled={!!saving} className="bg-primary hover:bg-primary/90">
              {saving === 'services' ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}Save Services
            </Button>
            <Button data-testid="add-service" variant="outline" onClick={() => {
              const id = prompt('Service ID (lowercase, no spaces, e.g., "llama3"):');
              if (!id || !id.match(/^[a-z0-9_]+$/)) { if (id) toast.error('ID must be lowercase letters, numbers, underscores only'); return; }
              if (config.services && config.services[id]) { toast.error('Service ID already exists'); return; }
              const newSvc = {
                name: id.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
                category: 'ml_model', description: '', friendly_description: '', use_cases: [], is_available: true,
                pricing: { economy: { hourly_rate: 0.05, max_latency_seconds: 10, max_requests_per_day: 1000, gpu_call_timeout_seconds: 30, priority: 1 }, standard: { hourly_rate: 0.10, max_latency_seconds: 5, max_requests_per_day: 10000, gpu_call_timeout_seconds: 30, priority: 5 }, priority: { hourly_rate: 0.20, max_latency_seconds: 2, max_requests_per_day: 100000, gpu_call_timeout_seconds: 30, priority: 10 } },
                feature_page: { tagline: '', overview: '', ai_development: [], how_it_works: '', works_with: [] }
              };
              updateField(`services.${id}`, newSvc);
              setExpandedSvc(p => ({ ...p, [id]: true }));
              toast.success(`Service "${id}" added. Fill in the details and click Save.`);
            }}><Plus className="w-4 h-4 mr-2" />Add New Service</Button>
          </div>
        </TabsContent>

        <TabsContent value="plans" className="space-y-4 mt-4">
          {config.plan_limits && Object.entries(config.plan_limits).map(([plan, limits]) => (
            <Card key={plan} data-testid={`config-plan-${plan}`} className="bg-card border-border/50">
              <CardHeader className="pb-3">
                <CardTitle className="text-base capitalize">{plan}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { key: 'rpm', label: 'Req/Min' },
                    { key: 'rpd', label: 'Req/Day' },
                    { key: 'max_services', label: 'Max Services' },
                    { key: 'max_keys', label: 'Max Keys' },
                  ].map(f => (
                    <div key={f.key} className="space-y-1">
                      <Label className="text-xs">{f.label}</Label>
                      <Input
                        data-testid={`plan-${plan}-${f.key}`}
                        type="number"
                        value={limits[f.key]}
                        onChange={e => updateField(`plan_limits.${plan}.${f.key}`, parseInt(e.target.value) || 0)}
                        className="h-8 text-xs font-mono"
                      />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
          <Button data-testid="save-plans" onClick={() => saveSection('plan_limits')} disabled={!!saving} className="bg-primary hover:bg-primary/90">
            {saving === 'plan_limits' ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Save Plans
          </Button>
        </TabsContent>

        <TabsContent value="gpu" className="space-y-4 mt-4">
          <Card className="bg-card border-border/50">
            <CardHeader>
              <CardTitle className="text-base">GPU Server Connection</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="space-y-1">
                <Label className="text-xs">Base URL</Label>
                <Input
                  data-testid="gpu-base-url"
                  value={config.gpu_server?.base_url || ''}
                  onChange={e => updateField('gpu_server.base_url', e.target.value)}
                  className="font-mono text-sm"
                  placeholder="https://gpu.yourdomain.com"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Internal API Key</Label>
                <Input
                  data-testid="gpu-api-key"
                  type="password"
                  value={config.gpu_server?.internal_api_key || ''}
                  onChange={e => updateField('gpu_server.internal_api_key', e.target.value)}
                  className="font-mono text-sm"
                />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border/50">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Stripe Configuration</CardTitle>
                <div className="flex items-center gap-2">
                  <Label className="text-xs text-muted-foreground">Mode:</Label>
                  <div className="flex rounded-lg overflow-hidden border border-border">
                    <button
                      data-testid="stripe-mode-test"
                      onClick={() => updateField('stripe.mode', 'test')}
                      className={`px-3 py-1 text-xs font-medium transition-colors ${(config.stripe?.mode || 'test') === 'test' ? 'bg-amber-500/20 text-amber-400' : 'text-muted-foreground hover:text-foreground'}`}
                    >Test</button>
                    <button
                      data-testid="stripe-mode-live"
                      onClick={() => updateField('stripe.mode', 'live')}
                      className={`px-3 py-1 text-xs font-medium transition-colors ${config.stripe?.mode === 'live' ? 'bg-emerald-500/20 text-emerald-400' : 'text-muted-foreground hover:text-foreground'}`}
                    >Live</button>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-xs text-muted-foreground">
                {(config.stripe?.mode || 'test') === 'live'
                  ? 'LIVE MODE — Real charges will be processed. Use pk_live_ and sk_live_ keys.'
                  : 'TEST MODE — No real charges. Use pk_test_ and sk_test_ keys from your Stripe Dashboard.'}
              </p>

              {/* Test Keys */}
              <div className={`space-y-3 p-4 rounded-xl border ${(config.stripe?.mode || 'test') === 'test' ? 'border-amber-500/30 bg-amber-500/5' : 'border-border/30 opacity-60'}`}>
                <p className="text-xs font-bold uppercase tracking-wider text-amber-400">Sandbox / Test Keys</p>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Publishable Key <span className="text-muted-foreground">(pk_test_...)</span></Label>
                    <Input data-testid="stripe-test-pk" value={config.stripe?.test_publishable_key || ''} onChange={e => updateField('stripe.test_publishable_key', e.target.value)} className="font-mono text-xs" placeholder="pk_test_..." />
                    <p className="text-[10px] text-muted-foreground">Client-side. Safe to expose in frontend.</p>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Secret Key <span className="text-muted-foreground">(sk_test_...)</span></Label>
                    <Input data-testid="stripe-test-sk" type="password" value={config.stripe?.test_secret_key || ''} onChange={e => updateField('stripe.test_secret_key', e.target.value)} className="font-mono text-xs" placeholder="sk_test_..." />
                    <p className="text-[10px] text-muted-foreground">Server-side only. Stored encrypted.</p>
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Test Webhook Secret</Label>
                  <Input data-testid="stripe-test-wh" type="password" value={config.stripe?.test_webhook_secret || ''} onChange={e => updateField('stripe.test_webhook_secret', e.target.value)} className="font-mono text-xs" placeholder="whsec_..." />
                </div>
              </div>

              {/* Live Keys */}
              <div className={`space-y-3 p-4 rounded-xl border ${config.stripe?.mode === 'live' ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-border/30 opacity-60'}`}>
                <p className="text-xs font-bold uppercase tracking-wider text-emerald-400">Production / Live Keys</p>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Publishable Key <span className="text-muted-foreground">(pk_live_...)</span></Label>
                    <Input data-testid="stripe-live-pk" value={config.stripe?.live_publishable_key || ''} onChange={e => updateField('stripe.live_publishable_key', e.target.value)} className="font-mono text-xs" placeholder="pk_live_..." />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Secret Key <span className="text-muted-foreground">(sk_live_...)</span></Label>
                    <Input data-testid="stripe-live-sk" type="password" value={config.stripe?.live_secret_key || ''} onChange={e => updateField('stripe.live_secret_key', e.target.value)} className="font-mono text-xs" placeholder="sk_live_..." />
                    <p className="text-[10px] text-muted-foreground">Server-side only. Stored encrypted. Shown once in Stripe Dashboard.</p>
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Live Webhook Secret</Label>
                  <Input data-testid="stripe-live-wh" type="password" value={config.stripe?.live_webhook_secret || ''} onChange={e => updateField('stripe.live_webhook_secret', e.target.value)} className="font-mono text-xs" placeholder="whsec_..." />
                </div>
              </div>

            </CardContent>
          </Card>
          <Button data-testid="save-gpu-config" onClick={async () => { await saveSection('gpu_server'); await saveSection('stripe'); }} disabled={!!saving} className="bg-primary hover:bg-primary/90">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Save Server Config
          </Button>
        </TabsContent>

        {/* Gateway Tab */}
        <TabsContent value="gateway" className="space-y-4 mt-4">
          <Card className="bg-card border-border/50">
            <CardHeader><CardTitle className="text-base">Gateway Settings</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs">API Timeout (seconds)</Label>
                  <Input data-testid="api-timeout" type="number" step="10" value={config.gateway?.api_timeout_seconds || 600} onChange={e => updateField('gateway.api_timeout_seconds', parseInt(e.target.value) || 600)} className="h-8 text-sm font-mono" />
                  <p className="text-[10px] text-muted-foreground">Maximum time to wait for GPU server response. Same for all users regardless of tier. If GPU server doesn't respond within this time, the request fails with 504.</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Max Concurrent Requests</Label>
                  <Input data-testid="max-concurrent" type="number" step="1" value={config.gateway?.max_concurrent_requests || 20} onChange={e => updateField('gateway.max_concurrent_requests', parseInt(e.target.value) || 20)} className="h-8 text-sm font-mono" />
                  <p className="text-[10px] text-muted-foreground">Maximum simultaneous requests to GPU server. When exceeded, new requests are queued by tier priority. Priority tier users get served first.</p>
                </div>
              </div>
              <div className="p-3 rounded-lg bg-secondary/20 border border-border/20 text-xs text-muted-foreground space-y-1">
                <p className="font-medium text-foreground">How Priority Queue Works:</p>
                <p>When concurrent requests exceed the limit, incoming requests wait in a priority queue.</p>
                <p>Priority tier → processed first (shortest wait). Standard → processed second. Economy → processed last.</p>
                <p>Each tier's "Max Latency" (configured per service) is the maximum queue wait time before returning a 503 "Service busy" error.</p>
              </div>
            </CardContent>
          </Card>
          <Button onClick={() => saveSection('gateway')} disabled={!!saving} className="bg-primary hover:bg-primary/90">
            {saving === 'gateway' ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}Save Gateway Settings
          </Button>

          {/* Welcome Credit */}
          <Card className="bg-card border-border/50">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Welcome Credit (New Signups)</CardTitle>
                <Switch data-testid="welcome-credit-toggle" checked={config.welcome_credit?.enabled || false} onCheckedChange={v => updateField('welcome_credit.enabled', v)} />
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs">Credit Amount ($)</Label>
                  <Input data-testid="welcome-credit-amount" type="number" step="10" value={config.welcome_credit?.amount || 100} onChange={e => updateField('welcome_credit.amount', parseFloat(e.target.value) || 0)} className="h-8 text-sm font-mono" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Description (shown in wallet)</Label>
                  <Input value={config.welcome_credit?.description || ''} onChange={e => updateField('welcome_credit.description', e.target.value)} className="h-8 text-sm" placeholder="Welcome credit — $100 free" />
                </div>
              </div>
              <p className="text-[10px] text-muted-foreground">Credited automatically to every new user's wallet on signup. Set amount to 0 or disable to stop offering free credit. Only affects new signups — existing user balances are unchanged.</p>
            </CardContent>
          </Card>
          <Button onClick={() => saveSection('welcome_credit')} disabled={!!saving} className="bg-primary hover:bg-primary/90">
            {saving === 'welcome_credit' ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}Save Welcome Credit
          </Button>
        </TabsContent>

        {/* LLM Providers Tab */}
        <TabsContent value="llm" className="space-y-4 mt-4">
          {/* Provider Cards */}
          {[
            { id: 'openrouter', name: 'OpenRouter', defaultUrl: 'https://openrouter.ai/api/v1', needsKeyForModels: false },
            { id: 'anthropic', name: 'Anthropic (Claude)', defaultUrl: 'https://api.anthropic.com/v1', needsKeyForModels: true },
          ].map(prov => (
            <Card key={prov.id} className="bg-card border-border/50">
              <CardHeader><CardTitle className="text-base">{prov.name}</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">API Key</Label>
                    <Input type="password" value={config.llm_providers?.[prov.id]?.api_key || ''} onChange={e => updateField(`llm_providers.${prov.id}.api_key`, e.target.value)} className="h-8 text-xs font-mono" placeholder={config.llm_providers?.[prov.id]?.api_key_enc ? '●●●●●●●● (key saved — enter new to replace)' : `${prov.id} API key`} />
                    <p className="text-[10px] text-muted-foreground">{config.llm_providers?.[prov.id]?.api_key_enc ? 'Key saved (encrypted). Leave empty to keep current key.' : 'No key configured. Enter API key and save.'}</p>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Base URL</Label>
                    <Input value={config.llm_providers?.[prov.id]?.base_url || prov.defaultUrl} onChange={e => updateField(`llm_providers.${prov.id}.base_url`, e.target.value)} className="h-8 text-xs font-mono" />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" className="h-7 text-xs" disabled={fetchingModels === prov.id || (prov.needsKeyForModels && !config.llm_providers?.[prov.id]?.api_key)}
                    onClick={async () => {
                      setFetchingModels(prov.id);
                      try {
                        // Save key first so backend can use it
                        await saveSection('llm_providers');
                        const res = await llmApi.fetchModels(prov.id);
                        setFetchedModels(prev => ({ ...prev, [prov.id]: res.data.models || [] }));
                        toast.success(`${res.data.count} models loaded from ${prov.name}`);
                      } catch (err) { toast.error(err.response?.data?.detail || `Failed to fetch ${prov.name} models`); }
                      setFetchingModels('');
                    }}>
                    {fetchingModels === prov.id ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Zap className="w-3 h-3 mr-1" />}
                    Fetch Models {fetchedModels[prov.id]?.length ? `(${fetchedModels[prov.id].length})` : ''}
                  </Button>
                  {prov.needsKeyForModels && !config.llm_providers?.[prov.id]?.api_key && (
                    <span className="text-[10px] text-muted-foreground">Add API key first to fetch models</span>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}

          {/* Tier → Model Mapping */}
          <Card className="bg-card border-border/50">
            <CardHeader><CardTitle className="text-base">Tier → Model Mapping</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              {['economy', 'standard', 'priority'].map(tier => {
                const tierCfg = config.llm_tier_models?.[tier] || {};
                const selectedProvider = tierCfg.provider || '';
                const providerModels = fetchedModels[selectedProvider] || [];
                const tierLabelsMap = { economy: 'Economy', standard: 'Standard', priority: 'Priority' };
                const tierColors = { economy: 'text-slate-400', standard: 'text-cyan-400', priority: 'text-amber-400' };
                return (
                  <div key={tier} className="p-4 rounded-xl bg-secondary/20 border border-border/20 space-y-3">
                    <p className={`text-xs font-bold uppercase tracking-wider ${tierColors[tier]}`}>{tierLabelsMap[tier]}</p>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Provider</Label>
                        <Select value={selectedProvider} onValueChange={v => { updateField(`llm_tier_models.${tier}.provider`, v); updateField(`llm_tier_models.${tier}.model`, ''); }}>
                          <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select provider" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="openrouter">OpenRouter</SelectItem>
                            <SelectItem value="anthropic">Anthropic (Claude)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Model {providerModels.length > 0 && `(${providerModels.length})`}</Label>
                        <div className="relative">
                          <Input value={tierCfg.model || ''} onChange={e => { updateField(`llm_tier_models.${tier}.model`, e.target.value); setModelSearch(e.target.value); }}
                            className="h-8 text-xs font-mono" placeholder={selectedProvider ? 'Type to search or select...' : 'Select provider first'} />
                          {modelSearch && tierCfg.model === modelSearch && providerModels.length > 0 && (
                            <div className="absolute top-9 left-0 right-0 z-50 max-h-48 overflow-y-auto bg-card border border-border rounded-lg shadow-xl">
                              {providerModels.filter(m => m.id.toLowerCase().includes(modelSearch.toLowerCase()) || m.name.toLowerCase().includes(modelSearch.toLowerCase())).slice(0, 20).map(m => (
                                <button key={m.id} className="w-full text-left px-3 py-1.5 text-xs hover:bg-secondary/50 border-b border-border/10 last:border-0"
                                  onClick={() => {
                                    updateField(`llm_tier_models.${tier}.model`, m.id);
                                    // Auto-fill max_tokens from model data
                                    const modelMax = m.max_tokens || m.context_length || 0;
                                    if (modelMax > 0) updateField(`llm_tier_models.${tier}.max_tokens`, modelMax);
                                    setModelSearch('');
                                  }}>
                                  <span className="font-mono font-medium">{m.id}</span>
                                  <span className="text-muted-foreground ml-2">{m.name !== m.id ? m.name : ''}</span>
                                  {(m.max_tokens || m.context_length) ? <span className="text-muted-foreground ml-1 text-[9px]">({((m.max_tokens || m.context_length) / 1000).toFixed(0)}K tokens)</span> : null}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Max Tokens</Label>
                        <div className="flex gap-1.5">
                          <Input type="number" value={tierCfg.max_tokens === -1 ? '' : (tierCfg.max_tokens || 4096)} onChange={e => updateField(`llm_tier_models.${tier}.max_tokens`, e.target.value === '' ? -1 : (parseInt(e.target.value) || 4096))}
                            className="h-8 text-xs font-mono flex-1" placeholder={tierCfg.max_tokens === -1 ? 'Model max' : ''} />
                          <button onClick={() => updateField(`llm_tier_models.${tier}.max_tokens`, -1)}
                            className={`px-2 h-8 rounded-md border text-[10px] font-medium transition-all ${tierCfg.max_tokens === -1 ? 'bg-primary/15 text-primary border-primary/40' : 'border-border/40 text-muted-foreground hover:text-foreground'}`}>
                            Max
                          </button>
                        </div>
                        <p className="text-[9px] text-muted-foreground">{tierCfg.max_tokens === -1 ? 'Using model\'s maximum output tokens' : `${tierCfg.max_tokens || 4096} tokens per response`}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>

          <Button onClick={async () => { await saveSection('llm_providers'); await saveSection('llm_tier_models'); await saveSection('llm_timeout_seconds'); }} disabled={!!saving} className="bg-primary hover:bg-primary/90">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}Save LLM Config
          </Button>

          {/* LLM Timeout */}
          <Card className="bg-card border-border/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className="space-y-1 flex-1">
                  <Label className="text-xs">LLM Response Timeout (seconds)</Label>
                  <Input type="number" min="30" max="600" value={config.llm_timeout_seconds || 300} onChange={e => updateField('llm_timeout_seconds', parseInt(e.target.value) || 300)} className="h-8 text-xs font-mono w-32" />
                </div>
                <p className="text-[10px] text-muted-foreground flex-1">Maximum time to wait for an LLM response. Large models with long contexts (Claude Opus, GPT-4) may need 300+ seconds. Default: 300s.</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Email Tab */}
        <TabsContent value="email" className="space-y-4 mt-4">
          <Card className="bg-card border-border/50">
            <CardHeader><CardTitle className="text-base">SMTP Configuration</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">SMTP Host</Label>
                  <Input data-testid="smtp-host" value={config.email?.smtp_host || ''} onChange={e => updateField('email.smtp_host', e.target.value)} className="font-mono text-sm" placeholder="smtp.gmail.com" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">SMTP Port</Label>
                  <Input data-testid="smtp-port" type="number" value={config.email?.smtp_port || 587} onChange={e => updateField('email.smtp_port', parseInt(e.target.value) || 587)} className="font-mono text-sm" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">SMTP Username</Label>
                  <Input data-testid="smtp-username" value={config.email?.smtp_username || ''} onChange={e => updateField('email.smtp_username', e.target.value)} className="font-mono text-sm" placeholder="username or email" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">SMTP Password</Label>
                  <Input data-testid="smtp-password" type="password" value={config.email?.smtp_password || ''} onChange={e => updateField('email.smtp_password', e.target.value)} className="font-mono text-sm" />
                  <p className="text-[10px] text-muted-foreground">Stored encrypted</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">From Name</Label>
                  <Input value={config.email?.from_name || ''} onChange={e => updateField('email.from_name', e.target.value)} className="text-sm" placeholder="GPU AI Platform" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">From Email</Label>
                  <Input value={config.email?.from_email || ''} onChange={e => updateField('email.from_email', e.target.value)} className="font-mono text-sm" placeholder="notifications@yourdomain.com" />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={config.email?.use_tls !== false} onCheckedChange={v => updateField('email.use_tls', v)} />
                <Label className="text-xs">Use TLS</Label>
              </div>
            </CardContent>
          </Card>
          <div className="flex gap-2">
            <Button onClick={() => saveSection('email')} disabled={!!saving} className="bg-primary hover:bg-primary/90">
              {saving === 'email' ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}Save Email Config
            </Button>
            <Button variant="outline" onClick={async () => {
              const email = prompt('Send test email to:');
              if (!email) return;
              try {
                await saveSection('email');
                const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/email/test`, {
                  method: 'POST', headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
                  body: JSON.stringify({ to_email: email })
                });
                const data = await res.json();
                if (data.success) toast.success('Test email sent!');
                else toast.error(`Failed: ${data.message}`);
              } catch { toast.error('Failed to send test email'); }
            }}><Send className="w-3 h-3 mr-1.5" />Send Test Email</Button>
          </div>

          {/* Email Template Editor */}
          <Separator className="my-4" />
          <Card className="bg-card border-border/50">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Email Templates</CardTitle>
                <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
                  <SelectTrigger className="w-64 h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(TEMPLATE_META).map(([key, meta]) => (
                      <SelectItem key={key} value={key} className="text-xs">{meta.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {(() => {
                const templates = config.email_templates || {};
                const tmpl = templates[selectedTemplate] || {};
                const meta = TEMPLATE_META[selectedTemplate] || {};
                return (
                  <>
                    <div className="space-y-1">
                      <Label className="text-xs">Subject Line</Label>
                      <Input
                        data-testid={`tmpl-subject-${selectedTemplate}`}
                        value={tmpl.subject || ''}
                        onChange={e => updateField(`email_templates.${selectedTemplate}.subject`, e.target.value)}
                        className="text-sm"
                        placeholder="Email subject..."
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Body</Label>
                      <textarea
                        data-testid={`tmpl-body-${selectedTemplate}`}
                        value={tmpl.body || ''}
                        onChange={e => updateField(`email_templates.${selectedTemplate}.body`, e.target.value)}
                        className="w-full bg-background border border-input rounded-md p-3 text-sm font-mono resize-none h-48 leading-relaxed"
                        placeholder="Email body..."
                      />
                    </div>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-[10px] text-muted-foreground">Variables:</span>
                      {(meta.vars || []).map(v => (
                        <span key={v} className="px-2 py-0.5 rounded-md bg-primary/10 text-primary text-[10px] font-mono cursor-pointer hover:bg-primary/20"
                          onClick={() => {
                            const ta = document.querySelector(`[data-testid="tmpl-body-${selectedTemplate}"]`);
                            if (ta) { const pos = ta.selectionStart; const val = tmpl.body || ''; updateField(`email_templates.${selectedTemplate}.body`, val.slice(0, pos) + v + val.slice(pos)); }
                          }}>{v}</span>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => saveSection('email_templates')} disabled={!!saving} className="bg-primary hover:bg-primary/90">
                        {saving === 'email_templates' ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />}Save Template
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => {
                        const defaults = {
                          welcome: { subject: "Welcome to GPU AI Platform", body: "Hi {user_name},\n\nWelcome to GPU AI Platform! Your account is ready.\n\nNext steps:\n1. Enable services in your dashboard\n2. Create an API key\n3. Add credit to your wallet ($10 minimum)\n4. Start making API calls\n\nGet started: {dashboard_url}\n\n— GPU AI Platform" },
                          low_balance_alert: { subject: "Low Balance Alert - GPU AI Platform", body: "Hi {user_name},\n\nYour wallet balance has dropped to ${balance}. Your low-balance threshold is ${threshold}.\n\nWhen your balance reaches $0, API calls will be blocked until you add credit.\n\nAdd credit now: {topup_url}\n\n— GPU AI Platform" },
                          balance_depleted: { subject: "Action Required: Wallet Empty - API Calls Blocked", body: "Hi {user_name},\n\nYour wallet balance has reached $0.00. All API calls are now blocked.\n\nTo restore service, add credit immediately: {topup_url}\n\n— GPU AI Platform" },
                          topup_confirmation: { subject: "Wallet Top-Up Confirmed - ${amount}", body: "Hi {user_name},\n\nYour wallet has been credited with ${amount}.\n\nNew balance: ${new_balance}\n\nView your wallet: {dashboard_url}\n\n— GPU AI Platform" },
                          password_reset: { subject: "Password Reset - GPU AI Platform", body: "Hi {user_name},\n\nYou requested a password reset. Use this token:\n\n{token}\n\nThis token expires in 30 minutes.\n\nReset here: {reset_url}\n\n— GPU AI Platform" },
                          auto_topup_failed: { subject: "Auto Top-Up Failed - Action Required", body: "Hi {user_name},\n\nYour auto top-up of ${amount} failed. Current balance: ${balance}.\n\nPlease update your payment method or add credit manually: {topup_url}\n\n— GPU AI Platform" },
                          new_signup_admin: { subject: "New User Signup - {user_name}", body: "New user signed up:\n\nName: {user_name}\nEmail: {email}\nDate: {signup_date}\n\n— GPU AI Platform Admin" },
                        };
                        if (defaults[selectedTemplate]) updateField(`email_templates.${selectedTemplate}`, defaults[selectedTemplate]);
                        toast.success('Reset to default');
                      }}><RotateCcw className="w-3 h-3 mr-1" />Reset to Default</Button>
                    </div>
                  </>
                );
              })()}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Card className="bg-card border-border/50">
        <CardHeader>
          <CardTitle className="text-sm text-muted-foreground">Raw Config (JSON)</CardTitle>
        </CardHeader>
        <CardContent>
          <pre className="bg-background border border-border/50 rounded-lg p-4 overflow-x-auto text-xs font-mono max-h-64">
            {JSON.stringify(config, null, 2)}
          </pre>
        </CardContent>
      </Card>
    </div>
  );
}
